<template>
  <div class="flex flex-col gap-y-8 h-auto relative">
    <!-- Loading overlay -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg"
    >
      <div class="flex flex-col items-center gap-3">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
        ></div>
        <span class="text-gray-600 font-medium">Updating order...</span>
      </div>
    </div>

    <div class="flex justify-center items-center">
      <template v-if="isQuoteOrder">
        <div class="fs-4 font-weight-bolder order-title p-2">Quote pending</div>
      </template>
      <span v-if="order_no">
        <span class="font-bold text-xl">ORDER# {{ order_no }}</span>
      </span>
      <span class="font-bold text-xl" v-else> Order pending confirmation</span>
      &nbsp;
        <span class="text-xl font-light capitalize">{{
          order.customer_name
        }}</span>
      <div class="font-bold flex order-title p-2">
        <template v-if="isQuoteOrder && showQuoteButtons">
          <button
            class="btn btn-secondary mx-2 cursor-pointer fs-2"
            @click="provideQuoteModal = true"
          >
            Accept Quote
          </button>
          <button
            class="btn btn-danger mx-2 cursor-pointer fs-2"
            @click="handleRejectQuote()"
          >
            Reject Quote
          </button>
        </template>

        <button
          v-if="showProvideQuoteButton"
          class="btn btn-secondary mx-2 cursor-pointer fs-2"
          @click="handleProvideQuotePrice()"
        >
          Provide Quote
        </button>
      </div>
    </div>
    <TimeLine :orderStatus="order_item?.status" :is-order-status="true" />
    <!-- <pre>{{ order_item }}</pre> -->
    <div
      v-if="can('move-order')"
      class="flex justify-center items-center gap-x-2"
    >
      <Select
        v-model="move_order_to_status"
        class="w-full h-12 disabled:opacity-50 disabled:bg-gray-600"
        :disabled="!order_item.factory_id || isMovingOrder"
        :options="moveOrderOptions"
        label="text"
        placeholder="Select Updated Order Status"
      />
      <div class="relative w-full flex items-center">
        <input
          v-model="move_order_reason"
          type="text"
          :disabled="!order_item.factory_id || isMovingOrder"
          class="outline-none border border-gray-300 rounded-md h-12 pl-4 pr-24 mr-2 w-full disabled:opacity-50"
          placeholder="Move order reason"
        />
        <button
          @click="moveOrderStatus(order_item, order_item_index)"
          :disabled="!order_item.factory_id || isMovingOrder"
          class="absolute right-3 bg-black text-white px-4 py-2 rounded-md flex text-center items-center disabled:opacity-50 disabled:bg-gray-600"
          :title="
            order_item.factory_id
              ? 'Move Order'
              : 'Order cannot be moved until it is assigned to a factory.'
          "
          style="top: 50%; transform: translateY(-50%)"
        >
          <span v-if="isMovingOrder">Moving...</span>
          <span v-else>Move</span>
        </button>
      </div>
    </div>
    <!-- Enhanced Timeline Implementation -->
    <TimeLine
      :orderStatus="order_item.status"
      :isOrderStatus="false"
      :current-step="1"
      :total-items="allActivitiesCount"
      :direction="'vertical'"
      :alternating="false"
    >
      <TimeLineItem
        v-for="(status_activity, isaIdx) in order_item.status_activities"
        :key="`timeline-item-${isaIdx}`"
        :attribution="
          status_activity.status === ORDERINPRODUCTION ||
          status_activity.status === CUSTOMERREVIEW ||
          status_activity.status === ORDERCANCEL
            ? activityStatus[status_activity.status].message
            : status_activity.quality_control_status
        "
        :title="activityStatus[status_activity.status].title"
        :index="isaIdx"
        :total-items="allActivitiesCount"
        :alternating="false"
        :activity-date-time="
          formatDate(status_activity.created_at, 'HH:mm Do MMM YY')
        "
        :icon="getActivityIcon(status_activity.status)"
        :completed-icon="getCompletedIcon(status_activity.status)"
        :action-log="status_activity.action_log"
        :activity-user="status_activity.user"
      >
        <!-- :actions="getActivityActions(status_activity, isaIdx)" -->
        <TimeLineItemContent
          :activity_content="
            getActivityContent(
              status_activity.activity_items,
              status_activity.status,
            )
          "
          @show-preview="
            (image_url) => {
              console.log(
                'activity item index',
                getActivityItemIndex(status_activity.activity_items, image_url),
              );
              showPreview(
                status_activity.activity_items[
                  getActivityItemIndex(
                    status_activity.activity_items,
                    image_url,
                  )
                ],
              );
            }
          "
        >
          <span
            class="mb-2 flex gap-1"
            v-if="
              (
                (order_item.status === ORDERINPRODUCTION &&
                  status_activity.status === ORDERINPRODUCTION &&
                  !order_item.est_shipping_date) ||
                (order_item.status === ORDERINPRODUCTION &&
                  status_activity.status === QUALITYCONTROL)
              ) &&
              isaIdx === currentInProductionOrQCIndex
            "
          >
            <button
              v-if="can('enter-shipping-date')"
              @click="() => (enterShippingDates = true)"
              class="bg-primary px-3 py-2 text-white rounded-md hover:bg-primary-dark transition-colors !cursor-pointer flex gap-2"
            >
              <CalendarIcon class="h-5 w-5 text-white" />
              Enter Est. Shipping Date
            </button>
            <button
              v-if="can('enter-shipping-date')"
              @click="() => (enterShippingDetails = true)"
              class="ml-2 bg-primary px-3 py-2 text-white rounded-md hover:bg-primary-dark transition-colors !cursor-pointer flex gap-2"
            >
              <TruckIcon class="h-5 w-5 text-white" />
              Enter Shipping Details
            </button>
          </span>
          <div
            v-if="
              order_item.status === ORDERINPRODUCTION &&
              status_activity.status === ORDERINPRODUCTION &&
              order_item.est_shipping_date
            "
            class="bg-[#FAFAFA] p-5 rounded-md border border-gray-300 text-xs"
          >
            <div class="flex justify-between">
              <div class="flex flex-col-reverse">
                <ul class="relative">
                  <li
                    class="shipping-dates-list"
                    v-for="(date, date_idx) in reversedEstShippingDates(
                      order_item,
                    )"
                    :key="`shipping_date_${date_idx}`"
                  >
                    <span
                      class="text-muted"
                      v-if="date_idx == order_item.est_shipping_date.length - 1"
                      ><span class="font-medium"
                        >Estimated shipping date:
                      </span>
                      <span class="text-[#7F7F7F]">{{ date }}</span></span
                    >
                    <span class="text-muted" v-else
                      ><span class="font-medium"
                        >Updated estimated shipping date:
                      </span>
                      <span class="text-[#7F7F7F]">{{ date }}</span></span
                    >
                  </li>
                </ul>
              </div>
              <div
                class="flex justify-end h-max space-x-2"
                v-if="
                  ((order_item.status === ORDERINPRODUCTION &&
                    status_activity.status === ORDERINPRODUCTION &&
                    !order_item.est_shipping_date) ||
                  (order_item.status === ORDERINPRODUCTION &&
                    status_activity.status !== QUALITYCONTROL)) &&
                  isaIdx === currentInProductionOrQCIndex
                "
              >
                <button
                  v-if="can('enter-shipping-date')"
                  @click="enterShippingDates = true"
                  class="p-2 bg-gray-100 hover:bg-gray-200 transition-colors border border-gray-300 rounded-md"
                >
                  <CalendarIcon class="h-5 w-5 text-gray-600" />
                </button>
                <button
                  v-if="
                    (() => {
                      const hasPermission = can('enter-order-shipping');
                      const statusCondition =
                        (order_item.status === ORDERINPRODUCTION &&
                          status_activity.status === ORDERINPRODUCTION) ||
                        status_activity.status === QUALITYCONTROL;
                      return hasPermission && statusCondition;
                    })() && isaIdx === currentInProductionOrQCIndex
                  "
                  @click="
                    () => {
                      enterShippingDetails = true;
                    }
                  "
                  class="p-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <TruckIcon class="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="
              order_item.status === FACTORYAPPROVED &&
              status_activity.status === FACTORYAPPROVED
            "
            class="mb-4"
          >
            <button
              @click="uploadSampleFiles(order_item, isaIdx, status_activity)"
              class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md flex items-center gap-2"
            >
              <ArrowUpOnSquareIcon class="size-5" />
              Upload Design Sample
            </button>
          </div>
          <div
            v-if="
              order_item.status == ORDERSHIPPED &&
              status_activity.status == ORDERSHIPPED
            "
            class="comment-button flex flex-col text-left flex-wrap px-2 gap-2"
          >
            <button
              @click="
                makeActivityData(
                  order_item,
                  order_item_index,
                  ORDERCOMPLETED,
                  false,
                )
              "
              class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md flex items-center gap-2"
            >
              <ArrowUpRightIcon class="size-5" />
              Complete Order
            </button>
          </div>
          <template
            v-if="
              order_item.status == FACTORYREVIEW &&
              status_activity.status == FACTORYREVIEW
            "
          >
            <button
              @click="
                makeActivityData(
                  order_item,
                  order_item_index,
                  ORDERAPPROVE,
                  false,
                )
              "
              class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md flex items-center gap-2"
            >
              <BuildingStorefrontIcon class="size-5" />
              Mark to factory
            </button>
          </template>
          <div
            v-if="
              ((order_item.status === CUSTOMERAPPROVED &&
                status_activity.status === CUSTOMERAPPROVED) ||
                (order_item.status === PRODUCTIONFILESUPLOADED &&
                  status_activity.status === PRODUCTIONFILESUPLOADED)) &&
              can('put-order-in-production') &&
              !isPutInProduction
            "
            class="mb-4"
          >
            <button
              @click="() => (isPutInProduction = true)"
              class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md flex items-center gap-2"
            >
              <svg
                viewBox="0 0 16 16"
                width="1em"
                height="1em"
                focusable="false"
                role="img"
                aria-label="printer"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                class="bi-printer"
              >
                <g>
                  <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"></path>
                  <path
                    d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z"
                  ></path>
                </g>
              </svg>
              Put In Production
            </button>
          </div>
          <template
            v-for="(activity_item, aiIdx) in status_activity.activity_items"
          >
            <div
              v-if="
                status_activity.activity_items &&
                status_activity.activity_items.length > 0
              "
              class="grid grid-cols-1"
            >
              <div
                :key="`activity-item-${aiIdx}`"
                class="relative"
                :class="evaluateClass(activity_item, status_activity.status)"
              >
                <div
                  v-if="
                    activity_item.activity_files &&
                    activity_item.activity_files.length > 0
                  "
                  class="flex gap-2 mb-3"
                >
                  <button
                    v-if="status_activity.status === PRODUCTIONFILESUPLOADED"
                    @click.stop="
                      downloadActivityProductionFile(
                        activity_item.activity_files,
                      )
                    "
                    class="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs flex items-center gap-1"
                  >
                    <svg style="width: 1em; height: 1em" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"
                      />
                    </svg>
                    Download
                  </button>
                </div>

                <div
                  v-if="
                    status_activity.status === ORDERSHIPPED &&
                    aiIdx === 0 &&
                    order_item.tracking_link
                  "
                  class="mb-2 text-sm w-full"
                >
                  The shipping link is
                  <a
                    :href="order_item.tracking_link"
                    target="_blank"
                    class="text-blue-500 hover:underline"
                  >
                    {{ order_item.tracking_link }}
                  </a>
                </div>
              </div>
            </div>
          </template>
        </TimeLineItemContent>
      </TimeLineItem>
    </TimeLine>

    <!-- Shipping Details Modal - moved to root level -->
    <ShippingDetailsModal
      v-if="shouldShowModal"
      :show-shipping-date="Boolean(enterShippingDates)"
      :show-put-in-production="Boolean(isPutInProduction)"
      :show-shipping-details="Boolean(enterShippingDetails)"
      :order-item="order_item"
      :order="order"
      :status="ORDERINPRODUCTION"
      @close="
        () => {
          enterShippingDetails = false;
          enterShippingDates = false;
          isPutInProduction = false;
        }
      "
      @shipping-date-saved="handleShippingDateSaved"
      @put-in-production-saved="handlePutInProductionSaved"
      @shipping-details-saved="handleShippingDetailsSaved"
    />

    <Transition name="fade">
      <ImagePreview
        v-if="showImagePreview"
        :activity_item="selectedActivityItem"
        :order_no="order.order_no"
        @close="() => closePreviewModal()"
      />
    </Transition>
    <Transition name="fade">
      <!-- :key="`${modal_type}_${design_approval_activity_navigation_index}`" -->
      <UploadDesignSample
        :activity_items="activity_items"
        :modal-type="modal_type"
        v-if="isUploadDesignSample"
        :design_approval_activity_item_data="design_approval_activity_item_data"
        :design_approval_activity_navigation_index="
          design_approval_activity_navigation_index
        "
        :modal_type="modal_type"
        @close="() => (isUploadDesignSample = false)"
        @cancel="() => (isUploadDesignSample = false)"
        @skip-sample-approval="skipSampleApproval"
        @navigate-sample-files="navigateSampleFiles"
        @submit-sample-files="submitSampleFiles"
        @change-sample-file="changeSampleFile"
        @delete-file="deleteFile"
        @close-message="closeMessage"
        @update-reason="updateReason"
      />
    </Transition>
    <Transition name="fade">
      <!-- :key="`${modal_type}_${design_approval_activity_navigation_index}`" -->
      <UploadProductionFilesModal
        v-if="showProductionFilesModal"
        :activity_items="activity_items"
        :activity_navigation_index="activity_navigation_index"
        :modal_type="modal_type"
        @close="() => (showProductionFilesModal = false)"
        @cancel="() => (showProductionFilesModal = false)"
        @skip-sample-approval="skipSampleApproval"
        @navigate-sample-files="navigateSampleFiles"
        @submit-sample-files="submitSampleFiles"
        @change-sample-file="changeSampleFile"
        @delete-file="deleteFile"
        @close-message="closeMessage"
        @update-reason="updateReason"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import {
  activityStatus,
  can,
  CustimooOrderFlowStatuses,
  custom_product_path_info,
  findActivity,
  findActivityWithPosition,
  formatDate,
  mergeActivityArray,
  storage_url,
} from "@/composables";
import { showAlert, showToast } from "@/composables/swal";
import { useOrderDetail } from "@/composables/useOrderDetail";
import { http } from "@/interceptors/axiosRequestInterceptor";

import {
  addDays,
  format,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";
import { computed, ComputedRef, onMounted, reactive, ref } from "vue";
import ShippingDetailsModal from "../Modals/ShippingDetailsModal.vue";

import Select from "@/components/Shared/Select.vue";
import {
  ActivityItem,
  ActivityItemPayload,
  ApprovalActivityFile,
  DesignApprovalActivityItem,
  OrderDetail,
  OrderDetailItem,
  SkipCustomerApproval,
  StatusActivity,
} from "@/models";
import { useMainStore } from "@/store";
import {
  ArrowUpOnSquareIcon,
  BuildingStorefrontIcon,
  ChatBubbleOvalLeftIcon,
} from "@heroicons/vue/24/outline";
import {
  ArrowUpRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  CubeIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  FlagIcon,
  PencilIcon,
  TruckIcon,
} from "@heroicons/vue/24/solid";
import AddUpdateComment from "./components/AddUpdateComment.vue";
import CommentsListing from "./components/CommentsListing.vue";
import ImagePreview from "./components/ImagePreview.vue";
import UploadProductionFilesModal from "./components/Modals/UploadProductionFilesModal.vue";
import TimeLine from "./components/TimeLine.vue";
import TimeLineItem from "./components/TimeLineItem.vue";
import TimeLineItemContent from "./components/TimeLineItemContent.vue";
import UploadDesignSample from "./components/UploadDesignSample.vue";

interface Props {
  order: OrderDetail | null;
  order_item: OrderDetailItem;
  order_item_index: number;
  order_no: string;
  isLoading?: boolean;
  // order: OrderDetail;
}

onMounted(() => {
  if (can("upload-production-files-activity")) {
    moveOrderOptions.value.push({
      value: PRODUCTIONFILESUPLOADED,
      text: "Upload production files",
    });
  }
});

const emit = defineEmits<{
  orderMoved: [responseData: any];
  orderUpdated: [orderItem: OrderDetailItem];
}>();

const props = withDefaults(defineProps<Props>(), {
  order_item: null,
  order_no: "",
  order: null,
  isLoading: false,
});

const store = useMainStore();
const enterShippingDates = ref<Boolean>(false);
const enterShippingDetails = ref<Boolean>(false);
const est_shipping_date = ref<Date | string | null>(null);
const isPutInProduction = ref<Boolean>(false);
const showImagePreview = ref<Boolean>(false);
const isUploadDesignSample = ref<Boolean>(false);
const selectedActivityItem = ref<ActivityItem>(null);
const shipping_dates = ref<string[]>([]);
const design_approval_activity_item_data = ref<DesignApprovalActivityItem[]>(
  [],
);
const design_approval_activity_navigation_index = ref<number>(0);
const activity_item_data = ref<any[]>([]);
const modal_type = ref<string>();

// Move order functionality variables
const move_order_to_status = ref<
  string | { value: string; text: string } | null
>(null);
const move_order_reason = ref<string>("");
const isMovingOrder = ref<boolean>(false);

// Modal states
const showArtworkDesignModal = ref<boolean>(false);
const showSampleFilesModal = ref<boolean>(false);
const showProductionFilesModal = ref<boolean>(false);

// Modal data
const artworkDesignItems = ref<any[]>([]);
const sampleFilesItems = ref<any[]>([]);
const productionFilesItems = ref<any[]>([]);

// Corrected allActivitiesCount logic
const allActivitiesCount = computed(() => {
  return props.order_item?.status_activities &&
    props.order_item.status_activities.length > 0
    ? props.order_item.status_activities.length
    : 0;
});
const activity_items = reactive<ActivityItemPayload>({
  order_move: 0,
  order_item_id: null,
  order_item_index: null,
  order_item_status: null,
  tracking_link: null,
  tracking_no: "",
  shipping_company: null,
  reason: null,
  activity_item_data: null,
});

const activity_navigation_index = ref<number>(0);
// Debug computed for modal visibility
const shouldShowModal = computed(() => {
  const show =
    enterShippingDetails.value ||
    enterShippingDates.value ||
    isPutInProduction.value;
  return show;
});

const {
  isQuoteOrder,
  showQuoteButtons,
  showProvideQuoteButton,
  rejectQuote,
  provideQuotePrice,
  takeCommentAction,
} = useOrderDetail();

// Computed property for order data
const order: ComputedRef<OrderDetail> = computed(() => props.order);

// Quote methods
const handleRejectQuote = async () => {
  if (order.value) {
    try {
      await rejectQuote(order.value);
      // Emit event to notify parent component
      emit("orderMoved", { success: true, action: "quote_rejected" });
    } catch (error) {
      console.error("Error rejecting quote:", error);
    }
  }
};

const handleProvideQuotePrice = () => {
  if (order.value) {
    try {
      provideQuotePrice(order.value);
      // Emit event to notify parent component
      emit("orderMoved", { success: true, action: "quote_provided" });
    } catch (error) {
      console.error("Error providing quote:", error);
    }
  }
};

const {
  FACTORYREVIEW,
  ORDERAPPROVE,
  ORDERCANCEL,
  FACTORYAPPROVED,
  CUSTOMERREVIEW,
  CUSTOMERAPPROVED,
  CUSTOMERREJECTED,
  ORDERINPRODUCTION,
  ORDERSHIPPED,
  ORDERCOMPLETED,
  FACTORY_APPROVE_REJECT,
  PRODUCTIONFILESUPLOADED,
  QUALITYCONTROL,
  QUOTEPROVIDED,
} = CustimooOrderFlowStatuses;

const moveOrderOptions = ref([
  { value: FACTORYREVIEW, text: "Order Created" },
  { value: ORDERAPPROVE, text: "Mark to factory" },
  { value: ORDERCANCEL, text: "Order Cancelled" },
  { value: FACTORYAPPROVED, text: "Artwork Approval" },
  { value: FACTORY_APPROVE_REJECT, text: "Artwork Approval/Artwork Rejected" },
  { value: CUSTOMERREVIEW, text: "Customer Review" },
  { value: ORDERINPRODUCTION, text: "In Production" },
  { value: ORDERSHIPPED, text: "Order Shipped" },
  { value: ORDERCOMPLETED, text: "Order Completed" },
]);

const provideQuoteModal = ref(false);

const getActivityTitle = (activity, index) => {
  // Get the appropriate title based on status
  const activityStatus = {
    [FACTORYREVIEW]: {
      title: index === 0 ? "Order Created" : "Artwork Updated",
    },
    [ORDERAPPROVE]: { title: "Mark to Factory" },
    [ORDERCANCEL]: { title: "Order Cancelled" },
    [FACTORYAPPROVED]: { title: "Artwork Approval" },
    [CUSTOMERREVIEW]: { title: "Customer Review" },
    [CUSTOMERAPPROVED]: { title: "Customer Approved" },
    [CUSTOMERREJECTED]: { title: "Customer Rejected" },
    [ORDERINPRODUCTION]: { title: "In Production" },
    [ORDERSHIPPED]: { title: "Order Shipped" },
    [ORDERCOMPLETED]: { title: "Order Completed" },
    [PRODUCTIONFILESUPLOADED]: { title: "Production Files Uploaded" },
    [QUALITYCONTROL]: { title: "Quality Control" },
    [QUOTEPROVIDED]: { title: "Quote Provided" },
  };

  return activityStatus[activity.status]?.title || "Unknown Status";
};

const getActivityImages = (activity_item) => {
  let groupedImages = [];
  activity_item?.forEach((item) => {
    groupedImages.push(
      item.activity_files?.map((file) => {
        return {
          url: file.url,
          alt: "product img",
          ext: getFileExtension(file.url),
        };
      }),
    );
  });
  store.$state.activity_item = activity_item;
  return groupedImages;
};

const getActivityContent = (
  activity_items: ActivityItem[],
  activity_status: string,
) => {
  let content_group = [];
  activity_items?.forEach((item, index) => {
    content_group.push({
      images:
        item.activity_files?.map((file) => {
          return {
            url: file.url,
            alt: "product img",
            ext: getFileExtension(file.url),
          };
        }) ?? [],
      nickName:
        props.order.additional_fields &&
        props.order.additional_fields.is_manual_order
          ? props.order_item.factory_products[index]?.design_nick_name
          : null,
      addons:
        props.order_item.factory_products[index]?.addons.length > 0
          ? props.order_item.factory_products[index]?.addons
          : [],
      skipReason:
        item.skip_customer_approval && item.skip_customer_approval.reason
          ? item.skip_customer_approval.reason
          : null,
      message: item.message ?? null,
      status: item.status,
      reorder_message: makeReorderMessage(
        item.factory_product_id,
        props.order_item.factory_products,
      ),
    });
  });
  return { content_group, activity_status };
};

const getActivityIcon = (status) => {
  // Map status to appropriate icon
  const iconMap = {
    [FACTORYREVIEW]: PencilIcon,
    [ORDERAPPROVE]: DocumentTextIcon,
    [ORDERCANCEL]: ExclamationCircleIcon,
    [FACTORYAPPROVED]: CheckCircleIcon,
    [CUSTOMERREVIEW]: PencilIcon,
    [CUSTOMERAPPROVED]: CheckCircleIcon,
    [CUSTOMERREJECTED]: ExclamationCircleIcon,
    [ORDERINPRODUCTION]: CubeIcon,
    [ORDERSHIPPED]: TruckIcon,
    [ORDERCOMPLETED]: FlagIcon,
    [PRODUCTIONFILESUPLOADED]: DocumentTextIcon,
    [QUALITYCONTROL]: CheckCircleIcon,
    [QUOTEPROVIDED]: DocumentTextIcon,
  };

  return iconMap[status] || DocumentTextIcon;
};

const getCompletedIcon = (status) => {
  // For completed items, we might want to use a check icon for all
  return CheckCircleIcon;
};

const shouldShowSpecialContent = (activity) => {
  // Determine if special content should be shown based on status
  return [
    FACTORYAPPROVED,
    CUSTOMERAPPROVED,
    PRODUCTIONFILESUPLOADED,
    ORDERINPRODUCTION,
    ORDERSHIPPED,
  ].includes(activity.status);
};

// Sample Upload Modal Changes

const skipSampleApproval = () => {
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.show_reason_modal = true;
};
const closeMessage = (design_activity_item, design_activity_index) => {
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.show_reason_modal = false;
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.reason = null;
  design_approval_activity_item_data.value[
    design_approval_activity_navigation_index.value
  ].skip_customer_approval.design_customer_approval = true;
};

const updateReason = () => {
  if (design_approval_activity_item_data.value.length) {
    if (
      design_approval_activity_item_data.value.length - 1 !==
      design_approval_activity_navigation_index.value
    ) {
      if (
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.show_reason_modal &&
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.reason !== null &&
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.reason !== ""
      ) {
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.design_customer_approval = false;
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].skip_customer_approval.show_reason_modal = false;
        navigateSampleFiles("next", true);
      } else {
        showToast("Please provide a valid reason to skip", "warning");
      }
    } else {
      design_approval_activity_item_data.value[
        design_approval_activity_navigation_index.value
      ].skip_customer_approval.design_customer_approval = false;
      design_approval_activity_item_data.value[
        design_approval_activity_navigation_index.value
      ].skip_customer_approval.show_reason_modal = false;
      submitSampleFiles(true);
    }
  }
};

const navigateSampleFiles = (
  direction: "next" | "back",
  skip: boolean = false,
) => {
  if (direction === "next") {
    if (!skip && validateSampleFiles()) {
      if (modal_type.value === "test_sample") {
        let limit = design_approval_activity_item_data.value.length;
        if (design_approval_activity_navigation_index.value + 1 < limit) {
          design_approval_activity_navigation_index.value =
            design_approval_activity_navigation_index.value + 1;
        }
      } else {
        // Check if activity_items.activity_item_data is an array
        let limit = Array.isArray(activity_items.activity_item_data)
          ? activity_items.activity_item_data.length
          : 0;
        if (activity_navigation_index.value + 1 < limit) {
          activity_navigation_index.value =
            design_approval_activity_navigation_index.value + 1;
        }
      }
    } else if (skip) {
      if (modal_type.value === "test_sample") {
        let limit = design_approval_activity_item_data.value.length;
        if (design_approval_activity_navigation_index.value + 1 < limit) {
          design_approval_activity_navigation_index.value =
            design_approval_activity_navigation_index.value + 1;
        }
      } else {
        // Check if activity_items.activity_item_data is an array
        let limit = Array.isArray(activity_items.activity_item_data)
          ? activity_items.activity_item_data.length
          : 0;
        if (activity_navigation_index.value + 1 < limit) {
          activity_navigation_index.value =
            design_approval_activity_navigation_index.value + 1;
        }
      }
    } else {
      showToast("Please upload front/back sample file", "warning");
    }
  } else {
    if (modal_type.value === "test_sample") {
      if (design_approval_activity_navigation_index.value - 1 >= 0) {
        design_approval_activity_navigation_index.value--;
      }
    } else {
      if (activity_navigation_index.value - 1 >= 0) {
        activity_navigation_index.value--;
      }
    }
  }
};
const validateSampleFiles = () => {
  let valid = true;
  if (modal_type.value === "test_sample") {
    if (design_approval_activity_item_data.value.length > 0) {
      if (
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].files
      ) {
        design_approval_activity_item_data.value[
          design_approval_activity_navigation_index.value
        ].files.forEach((obj) => {
          if (obj.file === null) valid = false;
        });
      } else {
        valid = false;
      }
    }
  } else {
    if (
      Array.isArray(activity_items.activity_item_data) &&
      activity_items.activity_item_data.length > 0
    ) {
      if (
        activity_items.activity_item_data[activity_navigation_index.value]
          .files &&
        activity_items.activity_item_data[activity_navigation_index.value].files
          .length > 0
      )
        activity_items.activity_item_data[
          activity_navigation_index.value
        ].files.forEach((obj) => {
          if (obj.file === null) valid = false;
        });
    } else {
      valid = false;
    }
  }
  return valid;
};

const deleteFile = (act_item_index, file_index) => {
  if (modal_type.value === "test_sample") {
    design_approval_activity_item_data.value[act_item_index].files[
      file_index
    ].file = null;
  } else {
    activity_items.activity_item_data[act_item_index].files[file_index].file =
      null;
  }
};

const setSampleImage = (act_item_index, file_index) => {
  if (modal_type.value === "test_sample") {
    return URL.createObjectURL(
      design_approval_activity_item_data.value[act_item_index].files[file_index]
        .file,
    );
  } else {
    return URL.createObjectURL(
      activity_items.activity_item_data[act_item_index].files[file_index].file,
    );
  }
};

const changeSampleFile = (files, act_item_index, file_index) => {
  if (modal_type.value === "test_sample") {
    design_approval_activity_item_data.value[act_item_index].files[
      file_index
    ].file = files[0];
  } else {
    activity_items.activity_item_data[act_item_index].files[file_index].file =
      files[0];
  }
};
const submitSampleFiles = (skip = false) => {
  if (skip || validateSampleFiles()) {
    submitActivity("form_data");
  } else {
    showToast("Please upload front/back sample file", "warning");
  }
};

const resetActivity = async () => {
  modal_type.value = null;
  isUploadDesignSample.value = false;
  showProductionFilesModal.value = false;
  resetActivityData();
};
const resetActivityData = () => {
  // move_order_to_status = null;
  // move_order_reason = null;
  activity_items.order_move = 0;
  activity_items.order_item_id = null;
  activity_items.order_item_index = null;
  (activity_items.tracking_link = null),
    (activity_items.tracking_no = ""),
    (activity_items.shipping_company = null),
    (activity_items.activity_item_data = []);
  activity_navigation_index.value = null;
  activity_items.order_item_status = null;
  activity_items.reason = null;
  activity_item_data.value = [];
};
// Helper functions for comments
const getInitials = (name) => {
  if (!name) return "";

  const nameArray = name.split(" ");
  const firstLetter = name.charAt(0);
  let lastLetter = "";

  if (nameArray.length > 1) {
    lastLetter = nameArray[nameArray.length - 1].charAt(0);
  }

  return (firstLetter + lastLetter).toUpperCase();
};

const isImageFile = (extension) => {
  if (!extension) return false;
  return ["jpg", "jpeg", "png", "bmp", "gif", "webp"].includes(
    extension.toLowerCase(),
  );
};

const isPdfFile = (extension) => {
  if (!extension) return false;
  return ["pdf"].includes(extension.toLowerCase());
};

const hideCommentBox = (item, property) => {
  item[property] = false;
};

const handleCommentActionCompleted = (
  updatedComment,
  activity,
  commentIndex,
) => {
  // Replace the comment at the specified index with the updated comment
  activity.comments[commentIndex] = updatedComment;

  // Close the edit form
  activity.comments[commentIndex].edit_comment = false;
};

const canPerformCommentAction = (comment) => {
  // Check if the current user can perform actions on this comment
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  // User can perform actions if they are the comment author
  if (comment.user && currentUser && comment.user.id === currentUser.id) {
    return true;
  }

  // Or if they have admin privileges (you can customize this logic)
  return can("manage-comments");
};

const evaluateRole = (comment) => {
  if (
    !comment.user ||
    !comment.user.userroles ||
    !comment.user.userroles.length
  ) {
    return "";
  }

  return comment.user.userroles[0].name || "";
};

const deleteComment = (comment, activity) => {
  // Implement comment deletion logic
  if (confirm("Are you sure you want to delete this comment?")) {
    // In a real app, you would call an API to delete the comment
    const commentIndex = activity.comments.findIndex(
      (c) => c.id === comment.id,
    );
    if (commentIndex !== -1) {
      activity.comments.splice(commentIndex, 1);
    }
  }
};

const getFileExtension = (url) => {
  if (!url) return "";
  return url.split(".").pop().toLowerCase();
};

const evaluateClass = (order_item, status) => {
  if (order_item.activity_files && order_item.activity_files.length > 0) {
    if (
      order_item.skip_customer_approval &&
      status !== ORDERINPRODUCTION &&
      (order_item.skip_customer_approval?.design_customer_approval ===
        "false" ||
        order_item.skip_customer_approval?.design_customer_approval === false)
    ) {
      return "submit-block skipped";
    } else {
      return "submit-block";
    }
  } else {
    if (
      order_item.skip_customer_approval &&
      status !== ORDERINPRODUCTION &&
      (order_item.skip_customer_approval?.design_customer_approval ===
        "false" ||
        order_item.skip_customer_approval?.design_customer_approval === false)
    ) {
      return "skipped";
    } else {
      return "";
    }
  }
};

const showPreview = (activity_item: ActivityItem) => {
  showImagePreview.value = true;
  selectedActivityItem.value = activity_item;
};
const closePreviewModal = () => {
  showImagePreview.value = false;
  selectedActivityItem.value = null;
};

const downloadAttachment = (file) => {
  console.log("Download attachment:", file);
  // Implement download logic
};

const downloadActivityProductionFile = (files) => {
  console.log("Download files:", files);
};

const showOrderDetails = (event, order_item, factory_product_id, download) => {
  console.log(
    "Show order details:",
    event,
    order_item,
    factory_product_id,
    download,
  );
};

const makeReorderMessage = (factory_product_id, factory_products) => {
  // Placeholder for reorder message logic
  return "";
};

const uploadSampleFiles = (
  order_item: OrderDetailItem,
  activity_index: number,
  activity_items_arg: StatusActivity,
) => {
  activity_items_arg.order_item_id = order_item.id;
  activity_item_data.value = [];
  design_approval_activity_item_data.value = [];
  let activity_item = Object.assign(
    {},
    order_item.status_activities[activity_index],
  );
  let actItem = order_item.status_activities.find(
    (status_activity) => status_activity.status === CUSTOMERREVIEW,
  );

  for (let factProd of order_item.factory_products) {
    if (factProd.status !== CUSTOMERAPPROVED) {
      let product_images;
      if (factProd.front_image) {
        product_images = [factProd.front_image];
      }
      if (factProd.back_image) {
        product_images.push(factProd.back_image);
      }
      if (!factProd.front_image && !factProd.back_image) {
        product_images = [
          `${storage_url}/${custom_product_path_info.url}`,
          `${storage_url}/${custom_product_path_info.url}`,
        ];
      }
      let actObj: DesignApprovalActivityItem = {
        action: null,
        factory_product_id: null,
        files: [],
        message: null,
        product_files: [],
        rejected_files: [],
        rejected_msg: null,
        skip_customer_approval: {
          design_customer_approval: false,
          reason: null,
          role_name: null,
          show_reason_modal: false,
          user_id: null,
          username: null,
        },
        status: ORDERCOMPLETED,
      };
      actObj.action = null;
      actObj.status = CUSTOMERREVIEW;
      actObj.message = "";
      actObj.files = [];
      actObj.factory_product_id = factProd.id;
      actObj.product_files = factProd.is_custom_product
        ? [custom_product_path_info.url, custom_product_path_info.url]
        : product_images;
      actObj.rejected_files = [];
      actObj.rejected_msg = "";

      if (activity_item.status === CUSTOMERREJECTED) {
        activity_item.activity_items.forEach((fileObj) => {
          if (fileObj.factory_product_id === factProd.id) {
            actObj.rejected_files = [
              fileObj.activity_files[0].url,
              fileObj.activity_files[1].url,
            ];
            actObj.rejected_msg = fileObj.message;
          }
        });
      }

      let skipCustomerApproval: SkipCustomerApproval = {
        design_customer_approval: true,
        reason: null,
        show_reason_modal: false,
        username: store.user?.name,
        user_id: store.user?.id,
        role_name: store.user?.roles[0]?.name,
      };
      if (activity_item.status === CUSTOMERREJECTED) {
        let all_activity_Items = null;
        let status_to_check = FACTORYREVIEW;
        if (order.value && order.value.ecommerce_order_id) {
          status_to_check = ORDERAPPROVE;
        }

        let artwork_created_activity = findActivityWithPosition(
          order_item?.status_activities,
          status_to_check,
          0,
        );
        let submitted_customer_review_activity = findActivity(
          order_item?.status_activities,
          CUSTOMERREVIEW,
          artwork_created_activity?.activity_items.length,
        );

        if (submitted_customer_review_activity) {
          all_activity_Items = mergeActivityArray(
            activity_item.activity_items,
            artwork_created_activity.activity_items,
            submitted_customer_review_activity,
          );
        }

        let activityItem = all_activity_Items.find((activity_item) => {
          return activity_item.factory_product_id === factProd.id;
        });

        if (activityItem) {
          skipCustomerApproval.design_customer_approval =
            activityItem?.skip_customer_approval?.design_customer_approval ===
              "true" ||
            activityItem?.skip_customer_approval?.design_customer_approval ===
              true ||
            activityItem?.skip_customer_approval?.design_customer_approval === 1
              ? true
              : false;
          skipCustomerApproval.reason = null;
          skipCustomerApproval.show_reason_modal = false;
          skipCustomerApproval.username = store.user?.name;
          skipCustomerApproval.user_id = store.user?.id;
          skipCustomerApproval.role_name = store.user?.roles[0]?.name;
          if (activity_item.status === CUSTOMERREJECTED) {
            actItem.activity_items.forEach((fileObj) => {
              if (fileObj.factory_product_id === factProd.id) {
                actObj.rejected_files = [
                  fileObj.activity_files[0].url,
                  fileObj.activity_files[1].url,
                ];
              }
            });
          }
        }
      } else {
        if (factProd?.sku?.design_customer_approval !== undefined) {
          skipCustomerApproval.design_customer_approval =
            !!factProd.sku.design_customer_approval;
          skipCustomerApproval.reason = null;
          skipCustomerApproval.show_reason_modal = false;
          skipCustomerApproval.username = store.user?.name;
          skipCustomerApproval.user_id = store.user?.id;
          skipCustomerApproval.role_name = store.user?.roles[0]?.name;
        }
      }
      actObj.skip_customer_approval = skipCustomerApproval;
      let fileObj: ApprovalActivityFile = {
        file: null,
        file_type: "object",
        side: "front",
      };
      actObj.files.push(fileObj);

      let fileObj2: ApprovalActivityFile = { ...fileObj };
      fileObj2.side = "back";
      actObj.files.push(fileObj2);

      activity_item_data.value.push(actObj);
      if (factProd.sku?.design_customer_approval !== undefined) {
        if (
          (actObj &&
            actObj?.skip_customer_approval?.design_customer_approval ===
              true) ||
          actObj?.skip_customer_approval?.design_customer_approval === true
        ) {
          design_approval_activity_item_data.value.push(actObj);
        }
      } else {
        design_approval_activity_item_data.value.push(actObj);
      }
    }
  }
  modal_type.value = "test_sample";
  isUploadDesignSample.value = true;

  // Set up activity items for sample files upload
  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = activity_index;

  sampleFilesItems.value = [];

  for (const factProd of order_item.factory_products) {
    if (factProd.status !== CUSTOMERAPPROVED) {
      const actObj = {
        action: null,
        status: CUSTOMERREVIEW,
        message: "",
        files: [],
        factory_product_id: factProd.id,
        product_files: factProd.is_custom_product
          ? [factProd.front_image, factProd.back_image]
          : [factProd.front_image, factProd.back_image],
        rejected_files: [],
        rejected_msg: "",
        skip_customer_approval: {
          design_customer_approval: true,
          reason: null,
          show_reason_modal: false,
          username: JSON.parse(localStorage.getItem("user") || "{}")?.name,
          user_id: JSON.parse(localStorage.getItem("user") || "{}")?.id,
          role_name: JSON.parse(localStorage.getItem("user") || "{}")?.role
            ?.name,
        },
      };

      // Check if customer approval is required
      if (factProd?.sku?.design_customer_approval !== undefined) {
        actObj.skip_customer_approval.design_customer_approval =
          !!factProd.sku.design_customer_approval;
      }

      // Add file objects for front and back
      const frontFile = {
        file: null,
        file_type: "object",
        side: "front",
      };
      actObj.files.push(frontFile);

      const backFile = {
        file: null,
        file_type: "object",
        side: "back",
      };
      actObj.files.push(backFile);

      sampleFilesItems.value.push(actObj);
    }
  }

  // Show the modal
  showSampleFilesModal.value = true;
};

const putOrderInProduction = async (
  status,
  order_item,
  order,
  notify,
  is_production,
) => {
  try {
    // First add the shipping date
    // await addShippingDate(status, order_item, order, notify);
    await makeActivityData(
      order_item,
      props.order_item_index,
      ORDERINPRODUCTION,
      false,
      move_order_reason.value,
      false,
    );

    // Set default shipping date for next time (21 days from now) using date-fns
    const defaultDate = addDays(new Date(), 21);
    est_shipping_date.value = defaultDate;

    // Close the production modal
    isPutInProduction.value = false;
  } catch (error) {
    console.error("Error putting order in production:", error);
  }
};

const makeActivityData = (
  order_item,
  order_item_index,
  status,
  add_files,
  move_order_reason = null,
  manual_action = false,
  submitForm: boolean = true,
) => {
  // Validate shipping details for ORDERSHIPPED status
  if (status === ORDERSHIPPED && activity_items.order_move === 0) {
    if (!activity_items.shipping_company) {
      showToast("Please select shipping company", "warning");
      return false;
    }
    if (!activity_items.tracking_no) {
      showToast("Please provide tracking no.", "warning");
      return false;
    }
  }

  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = order_item_index;
  activity_items.order_item_status = status;
  activity_navigation_index.value = 0;

  activity_items.activity_item_data = [];

  // Process factory products
  for (let factProd of order_item.factory_products) {
    let actObj = {
      action: null,
      status: status,
      reason: move_order_reason,
      manual_action: manual_action,
      message: "",
      files: [],
      factory_product_id: factProd.id,
    };

    // Add files if requested
    if (add_files) {
      let front_file = {
        file: factProd ? factProd.front_image : null,
        file_type: "",
        side: "front",
      };
      actObj.files.push(front_file);

      let back_file = {
        file: factProd ? factProd.back_image : null,
        file_type: "",
        side: "back",
      };
      actObj.files.push(back_file);
    }

    activity_items.activity_item_data.push(actObj);
  }
  if (submitForm) {
    submitActivity("form_data");
  }
};

const showArtworkDesigns = (order_item, index, activity_index) => {
  console.log("Show artwork designs:", order_item, index, activity_index);

  // Set up activity items for artwork design review
  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = index;

  const activity_item = { ...order_item.status_activities[activity_index] };

  artworkDesignItems.value = [];

  for (const actItem of activity_item.activity_items) {
    const actObj = {
      action: null,
      status: activity_item.status,
      message: null,
      files: [],
      factory_product_id: actItem.factory_product_id,
      skip_customer_approval: {
        design_customer_approval: true,
        reason: null,
        show_reason_modal: false,
        username: JSON.parse(localStorage.getItem("user") || "{}")?.name,
        user_id: JSON.parse(localStorage.getItem("user") || "{}")?.id,
        role_name: JSON.parse(localStorage.getItem("user") || "{}")?.role?.name,
      },
    };

    // Add files from activity
    for (const actfile of actItem.activity_files) {
      if (actfile.url) {
        const fileObj = {
          file: actfile.url,
          file_type: null,
          side: actfile.side || "front",
        };
        actObj.files.push(fileObj);
      }
    }

    // Check if customer approval is required
    const factory_product = order_item.factory_products.find(
      (factory_product) => factory_product.id === actItem.factory_product_id,
    );

    if (factory_product?.sku?.design_customer_approval !== undefined) {
      actObj.skip_customer_approval.design_customer_approval =
        !!factory_product.sku.design_customer_approval;
    }

    artworkDesignItems.value.push(actObj);
  }

  // Show the modal
  showArtworkDesignModal.value = true;
};

const setTrackingLink = (event, type) => {
  console.log("Set tracking link:", event, type);
};

const addShippingDate = async (
  status: string,
  order_item: Record<any, any>,
  order?: Record<any, any>,
  notify: boolean = false,
) => {
  if (status === ORDERINPRODUCTION) {
    // Validate shipping date
    if (!est_shipping_date.value) {
      showAlert("ERROR", "Please select a shipping date", "error");
      return;
    }

    // Convert Date object to YYYY-MM-DD string format using date-fns
    let formattedDate: string;
    const dateValue = est_shipping_date.value;

    if (dateValue instanceof Date) {
      formattedDate = format(dateValue, "yyyy-MM-dd");
    } else if (typeof dateValue === "string") {
      // If it's already a string, ensure it's in the correct format
      const parsedDate = parseISO(dateValue);
      if (!isValid(parsedDate)) {
        showAlert("ERROR", "Invalid date format", "error");
        return;
      }
      formattedDate = format(parsedDate, "yyyy-MM-dd");
    } else {
      showAlert("ERROR", "Invalid date value", "error");
      return;
    }

    // Validate that the date is not in the past using date-fns
    const estShippingDate = parseISO(formattedDate);
    const today = startOfDay(new Date());

    if (isBefore(estShippingDate, today)) {
      showAlert(
        "ERROR",
        "Estimated shipping date cannot be in the past.",
        "error",
      );
      return;
    }

    shipping_dates.value = [...shipping_dates.value, formattedDate];

    let url = `customer-orders/${order_item.id}/est-shipping-date`;

    http
      .post(url, { shipping_dates: shipping_dates.value })
      .then(async (successResponse) => {
        if (notify) {
          if (status === ORDERINPRODUCTION) {
            let url = `notify-customer-shipping-date/${order_item.id}/order/${order.id}`;
            if (formattedDate) {
              http.post(url).then(async (successResponse) => {
                showAlert("Success", "Shipping date added", "success");
              });
            } else {
              showAlert("error", "Please save shipping date.", "error");
            }
          }
        }
        enterShippingDates.value = false;
        est_shipping_date.value = null;
        showAlert("Success", "Shipping date added", "success");

        // Emit event to notify parent component
        emit("orderMoved", {
          success: true,
          action: "shipping_date_added",
          date: formattedDate,
        });
      })
      .catch((errorResponse) => {
        console.log(errorResponse);
      });
  }
};

// Computed property for shipping options
const getShippingOptions = computed(() => {
  return [
    { value: "fedex", text: "FedEx" },
    { value: "ups", text: "UPS" },
    { value: "usps", text: "USPS" },
    { value: "dhl", text: "DHL" },
  ];
});
const getActivityItemIndex = (
  current_activity_items: ActivityItem[],
  image_url: string,
) => {
  return current_activity_items.findIndex((item) =>
    item.activity_files.find((file) => file.url === image_url),
  );
};

const reversedEstShippingDates = (order_item: any) => {
  return order_item.est_shipping_date
    ? [...order_item.est_shipping_date].reverse()
    : [];
};

// Helper function to format dates consistently using date-fns
const formatDateForDisplay = (dateString: string): string => {
  try {
    const parsedDate = parseISO(dateString);
    if (!isValid(parsedDate)) {
      return dateString; // Return original if invalid
    }
    return format(parsedDate, "dd/MM/yyyy");
  } catch (error) {
    return dateString; // Return original if error
  }
};

const handleShippingDateSaved = (date) => {
  // Handle the event when a shipping date is saved
  console.log("Shipping date saved:", date);
  // Emit event to notify parent component - use orderMoved since it's a status change
  emit("orderMoved", { success: true, action: "shipping_date_saved", date });
};

const handlePutInProductionSaved = (notify) => {
  putOrderInProduction(
    ORDERINPRODUCTION,
    props.order_item,
    props.order,
    notify,
    true,
  );
  // Handle the event when put in production is saved
  // Emit event to notify parent component
  emit("orderMoved", {
    success: true,
    action: "put_in_production",
    notify,
  });
};

const handleShippingDetailsSaved = (details) => {
  // Handle the event when shipping details are saved
  console.log("Shipping details saved:", details);

  // Update activity_items with shipping details
  activity_items.shipping_company = details.shipping_company;
  activity_items.tracking_no = details.tracking_no;
  activity_items.tracking_link = details.tracking_link;

  // Move order to shipped status
  makeActivityData(
    props.order_item,
    props.order_item_index,
    ORDERSHIPPED,
    false,
  );

  // Close the modal
  enterShippingDetails.value = false;

  // Emit event to notify parent component
  emit("orderMoved", {
    success: true,
    action: "shipping_details_saved",
    details,
  });
};

const submitActivity = async (submit_type: string) => {
  try {
    if (modal_type.value === "test_sample") {
      activity_items.activity_item_data =
        design_approval_activity_item_data.value ??
        activity_items.activity_item_data;
    }
    // Set loading state
    isMovingOrder.value = true;

    let form_data = new FormData();
    form_data.append("order_id", String(props.order?.id || ""));

    // Process activity items data
    for (const key in activity_items) {
      if (key === "activity_item_data" && Array.isArray(activity_items[key])) {
        activity_items[key].forEach(
          (activity_file_obj: any, actIndx: number) => {
            for (const key2 in activity_file_obj) {
              if (key2 === "files") {
                activity_file_obj[key2].forEach(
                  (activity_file: any, fileInd: number) => {
                    for (const key3 in activity_file) {
                      if (key3 === "file") {
                        form_data.append(
                          key +
                            "[" +
                            actIndx +
                            "][" +
                            key2 +
                            "][" +
                            fileInd +
                            "][" +
                            key3 +
                            "]",
                          activity_file[key3] || "",
                        );
                      }
                      if (key3 === "file_type") {
                        form_data.append(
                          key +
                            "[" +
                            actIndx +
                            "][" +
                            key2 +
                            "][" +
                            fileInd +
                            "][" +
                            key3 +
                            "]",
                          activity_file[key3] || "",
                        );
                      }
                    }
                  },
                );
              } else if (key2 === "skip_customer_approval") {
                Object.entries(activity_file_obj[key2]).forEach(
                  ([skipkey, skip_value]) => {
                    form_data.append(
                      key + "[" + actIndx + "][" + key2 + "][" + skipkey + "]",
                      skip_value as string,
                    );
                  },
                );
              } else {
                form_data.append(
                  key + "[" + actIndx + "][" + key2 + "]",
                  String(activity_file_obj[key2] || ""),
                );
              }
            }
          },
        );
      } else {
        form_data.append(key, String(activity_items[key] || ""));
      }
    }

    const url = `customer-orders/${activity_items.order_item_id}/order-activity`;
    const headers =
      submit_type === "form_data"
        ? {
            "Content-Type": "multipart/form-data",
          }
        : {};
    const response = await http.post(url, form_data, {
      headers,
    });
    const response_data = response.data;

    // Reset activity data
    resetActivity();

    // Emit events to notify parent component
    emit("orderMoved", response_data);
    if (response_data.result?.order_item) {
      emit("orderUpdated", response_data.result.order_item);
    }

    // Show success message
    showToast("Order moved successfully", "success");

    // Refresh order data
    if (props.order) {
      // You may need to emit an event to refresh the parent component
      console.log("Activity submitted successfully:", response_data);
    }
  } catch (errorResponse) {
    console.error("Error submitting activity:", errorResponse);
    // Handle error - you may want to show an alert
    showToast("Error moving order. Please try again.", "error");
  } finally {
    // Clear loading state
    isMovingOrder.value = false;
  }
};

// Move order functionality
const moveOrderStatus = (order_item: any, order_item_index: number) => {
  activity_items.order_move = 1;
  activity_items.order_item_status = null;

  if (move_order_to_status.value === null) {
    showToast("Please select move order option", "error");
    return false;
  } else if (!move_order_reason.value) {
    showToast("Please state reason for moving the order", "error");
    return false;
  } else {
    // Extract the actual value from the selected option object
    const selectedStatus = move_order_to_status.value
      ? typeof move_order_to_status.value === "object" &&
        "value" in move_order_to_status.value
        ? move_order_to_status.value.value
        : move_order_to_status.value
      : "";

    let add_files = false;

    if (selectedStatus === FACTORY_APPROVE_REJECT) {
      activity_items.order_item_status = ORDERAPPROVE;
      activity_navigation_index.value = 0;
      showArtworkDesigns(
        order_item,
        order_item_index,
        order_item.status_activities.length - 1,
      );
      return;
    } else if (selectedStatus === CUSTOMERREVIEW) {
      activity_items.order_item_status = CUSTOMERREVIEW;
      activity_navigation_index.value = 0;
      uploadSampleFiles(
        order_item,
        order_item_index,
        order_item.status_activities,
      );
      return;
    } else if (selectedStatus === FACTORYREVIEW) {
      add_files = true;
    } else if (selectedStatus === ORDERAPPROVE) {
      add_files = true;
    } else if (selectedStatus === PRODUCTIONFILESUPLOADED) {
      makeActivityData(
        order_item,
        order_item_index,
        selectedStatus,
        add_files,
        move_order_reason.value,
        true,
      );
      uploadProductionFiles(order_item, order_item_index, selectedStatus);
      move_order_to_status.value = null;
      move_order_reason.value = "";
      return;
    }

    makeActivityData(
      order_item,
      order_item_index,
      selectedStatus,
      add_files,
      move_order_reason.value,
      true,
    );
    move_order_to_status.value = null;
    move_order_reason.value = "";
  }
};

const uploadProductionFiles = (
  order_item: any,
  order_item_index: number,
  selectedStatus: string,
) => {
  // Set up activity items for production files upload
  activity_items.order_item_id = order_item.id;
  activity_items.order_item_index = order_item_index;
  activity_items.reason = move_order_reason.value || "";
  activity_items.order_item_status = selectedStatus;

  productionFilesItems.value = [];

  for (const factProd of order_item.factory_products) {
    const actObj = {
      action: null,
      status: selectedStatus,
      message: "",
      reason: activity_items.reason,
      manual_action: true,
      files: [],
      factory_product_id: factProd.id,
      product_files: factProd.is_custom_product
        ? [factProd.front_image, factProd.back_image]
        : [factProd.front_image, factProd.back_image],
    };

    // Add file object
    const fileObj = {
      file: null,
      file_type: "object",
      side: "front",
    };
    actObj.files.push(fileObj);

    productionFilesItems.value.push(actObj);
  }
  activity_items.activity_item_data = productionFilesItems.value;

  // Show the modal
  showProductionFilesModal.value = true;
};

// Modal handlers
const closeArtworkDesignModal = () => {
  showArtworkDesignModal.value = false;
  artworkDesignItems.value = [];
};

const handleArtworkDesignSubmit = (data: any) => {
  // Update activity items with the submitted data
  activity_items.activity_item_data = data;
  submitActivity("form_data");
  closeArtworkDesignModal();
};

const closeSampleFilesModal = () => {
  showSampleFilesModal.value = false;
  sampleFilesItems.value = [];
};

const handleSampleFilesSubmit = (data: any) => {
  // Update activity items with the submitted data
  activity_items.activity_item_data = data;
  submitActivity("form_data");
  closeSampleFilesModal();
};

const closeProductionFilesModal = () => {
  showProductionFilesModal.value = false;
  productionFilesItems.value = [];
};

const handleProductionFilesSubmit = (data: any) => {
  // Update activity items with the submitted data
  activity_items.activity_item_data = data;
  submitActivity("form_data");
  closeProductionFilesModal();
};

const currentInProductionOrQCIndex = computed(() => {
  if (!props.order_item?.status_activities) return -1;
  for (let i = 0; i < props.order_item.status_activities.length; i++) {
    const status = props.order_item.status_activities[i].status;
    if (status === ORDERINPRODUCTION || status === QUALITYCONTROL) {
      return i;
    }
  }
  return -1;
});
</script>

<style scoped>
.shipping-dates-list {
  padding-bottom: 1rem;
  border-left: 1px solid #4e4e4e;
  position: relative;
  padding-left: 20px;
  &:last-child {
    border: 0px;
    padding-bottom: 0;
  }
  &:before {
    content: "";
    width: 5px;
    height: 5px;
    background: #4e4e4e;
    border: 1px solid #4e4e4e;
    transform: rotate(45deg);
    position: absolute;
    left: -3px;
    top: 0;
  }
}
</style>
